import { createTheme } from '@mui/material/styles'

export const theme = createTheme({
  palette: {
    primary: {
      light: '#77a6f7',
      main: '#004a99',
      dark: '#1A2230',
    },
    secondary: {
      main: '#ffffff',
    },
    grey: {
      600: '#d3d3d3',
      700: '#a6a6a6',
      800: '#757575',
      900: '#414141',
      A100: '#cfcfcf',
      A200: '#262626',
      A300: '#F9F9F9',
    },
  },
  typography: {
    fontFamily:
      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", "Helvetica", Roboto, Arial, sans-serif',
    // fontFamily: '"Helvetica Neue", "Helvetica", Roboto, Arial, sans-serif',
    color: '#414141',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: '2rem',
      '@media (min-width:600px)': {
        fontSize: '3.25rem',
      },
      '@media (min-width:900px)': {
        fontSize: '3.5rem',
      },
      lineHeight: 1.3,
      marginBottom: '21px',
      fontWeight: 700,
      color: '#262626',
    },
    h2: {
      fontSize: '1.5rem',
      '@media (min-width:600px)': {
        fontSize: '2.25rem',
      },
      '@media (min-width:900px)': {
        fontSize: '2.5rem',
      },
      fontWeight: 500,
      marginBottom: '25px',
      color: '#262626',
    },
    h3: {
      fontSize: '1.2rem',
      '@media (min-width:600px)': {
        fontSize: '2rem',
      },
      '@media (min-width:900px)': {
        fontSize: '2.2rem',
      },
      fontWeight: 500,
      marginBottom: '25px',
      color: '#262626',
    },
    h4: {
      fontSize: '1.1rem',
      '@media (min-width:600px)': {
        fontSize: '1.5rem',
      },
      '@media (min-width:900px)': {
        fontSize: '1.75rem',
      },
      fontWeight: 500,
      marginBottom: '20px',
      color: '#262626',
    },
    h6: {
      fontSize: '1.17rem',
      '@media (min-width:600px)': {
        fontSize: '1.15rem',
      },
      '@media (min-width:900px)': {
        fontSize: '1.1rem',
      },
      fontWeight: 500,
      color: '#262626',
    },
    subtitle1: {
      fontWeight: 400,
      color: '#414141',
    },
    subtitle2: { fontWeight: 400, color: '#414141' },
    body1: { fontWeight: 300, color: '#414141' },
    body2: { fontSize: '.93rem', fontWeight: 300, color: '#414141' },
    caption: { fontWeight: 300, color: '#414141' },
    overline: { fontWeight: 300 },
  },
  // Reusable, cross-module style tokens consumed via sx={(t) => t.custom.*}.
  // Keeps the section title + caption block identical across home modules.
  custom: {
    sectionHead: {
      maxWidth: 760,
      mx: 'auto',
      textAlign: 'center',
      mb: { xs: 5, md: 7 },
    },
    sectionTitle: {
      color: 'primary.dark',
      mb: 2,
    },
    sectionSubtitle: {
      fontSize: { xs: 16, md: 20 },
      lineHeight: 1.6,
    },
    // Vertical stack wrapping the card rows.
    cardStack: {
      display: 'flex',
      flexDirection: 'column',
      gap: 3,
    },
    // Lead row: a wider blue heading cell followed by two equal cards.
    cardRowLead: {
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        md: '1.6fr 1fr 1fr',
      },
      gap: 3,
      alignItems: 'stretch',
    },
    // Standard row of three equal cards.
    cardRow: {
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
      },
      gap: 3,
      alignItems: 'stretch',
    },
    // Standard row of four equal cards.
    cardRowFour: {
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(4, 1fr)',
      },
      gap: 3,
      alignItems: 'stretch',
    },
    // Plain grey panel holding a centered paragraph — pairs with promoCard
    // in a lead row (blue heading cell + description cell).
    infoPanel: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      bgcolor: 'grey.A300',
      border: '1px solid #e8e8e8',
      borderRadius: '20px',
      p: { xs: 4, md: 5 },
    },
    // Solid blue card used as the lead-in heading cell of the lead row.
    promoCard: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      bgcolor: 'primary.main',
      borderRadius: '20px',
      p: { xs: 4, md: 5 },
    },
    promoCardTitle: {
      color: 'secondary.main',
      mb: 0,
    },
    // CTA banner: rounded blue card with a heading column and a
    // description + action column.
    ctaBlock: {
      position: 'relative',
      overflow: 'hidden',
      bgcolor: 'primary.main',
      borderRadius: '20px',
      p: { xs: 4, md: '64px 56px' },
    },
    ctaGrid: {
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
      gap: { xs: 3, md: 10 },
      alignItems: { xs: 'center', md: 'start' },
    },
    ctaTitle: {
      color: 'secondary.main',
      mb: 0,
    },
    ctaDescription: {
      color: 'secondary.main',
      fontSize: { xs: 15, md: 16 },
      lineHeight: 1.7,
      mb: 4,
    },
  },
})

export default theme
